import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  Menu,
  X,
  MapPin,
  Building2,
  Home,
  Landmark,
  Trees,
  HousePlus,
  ChevronDown,
  PhoneCall,
} from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import logo from "./media/logo1.png";
import { useNavigate } from "react-router-dom";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false); // for call popup
  const [user, setUser] = useState({ loggedIn: false, firstName: "", lastName: "" });
  const navigate = useNavigate();

  const userDropdownRef = useRef(null); // ref for dropdown button + menu

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



  useEffect(() => {
    const interval = setInterval(() => {
      const savedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (
        savedUser.firstname !== user.firstName ||
        savedUser.lastname !== user.lastName ||
        (!savedUser.firstname && user.loggedIn)
      ) {
        setUser({
          loggedIn: !!Object.keys(savedUser).length,
          firstName: savedUser.firstname || "",
          lastName: savedUser.lastname || "",
        });
      }
    }, 500);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser({ loggedIn: false, firstName: "", lastName: "" });
    navigate("/");
  };

  const handleExplore = (params) => {
    const search = new URLSearchParams(params).toString();
    navigate(`/explore-properties?${search}`);
    setIsMobileMenuOpen(false);
  };

  const investments = [
    { name: "1 BHK Apartments", price: "₹25-35 L", bhk: "1", icon: Home, color: "text-rose-500" },
    { name: "2 BHK Apartments", price: "₹45-65 L", bhk: "2", icon: Home, color: "text-indigo-500" },
    { name: "3 BHK Apartments", price: "₹75 L - 1.2 Cr", bhk: "3", icon: Home, color: "text-emerald-500" },
    { name: "Bungalows", price: "₹1.5 Cr - 3 Cr", type: "bungalow", icon: HousePlus, color: "text-orange-500" },
    { name: "Land / Plots", price: "₹15-50 L /plot", type: "plot", icon: Landmark, color: "text-teal-500" },
    { name: "Row Houses", price: "₹30-40 L /unit", type: "rowhouse", icon: Building2, color: "text-purple-500" },
    { name: "Farmhouses", price: "₹50 L - 1 Cr", type: "farmhouse", icon: Trees, color: "text-lime-500" },
  ];

  return (
    <header className="bg-white/95 backdrop-blur-sm sticky top-0 z-50 border-b border-neutral/10 shadow-sm">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
            <img src={logo} alt="Skyline Properties Logo" className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 object-contain drop-shadow-lg" />
            <div className="font-display-bold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-primary leading-tight whitespace-nowrap">
              Skyline&nbsp;Properties
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Investments Menu */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-primary hover:text-luxury">
                    <Building2 className="w-5 h-5 mr-2 text-gradient-to-r from-indigo-500 to-pink-500" />
                    Investments
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[520px] gap-3 p-4">
                      {investments.map((item, idx) => {
                        const Icon = item.icon;
                        return (
                          <NavigationMenuLink
                            key={idx}
                            onClick={() =>
                              item.bhk
                                ? handleExplore({ bhk: item.bhk })
                                : handleExplore({ propertyType: item.type })
                            }
                            className="flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline transition-colors hover:bg-accent hover:text-accent-foreground"
                          >
                            <Icon className={`w-6 h-6 ${item.color}`} />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-primary">{item.name}</div>
                              <p className="text-xs leading-snug text-muted-foreground">{item.price}</p>
                            </div>
                          </NavigationMenuLink>
                        );
                      })}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <a onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })} className="cursor-pointer text-primary hover:text-luxury">About</a>
            <a onClick={() => { document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" }); navigate("/explore-properties"); }} className="cursor-pointer text-primary hover:text-luxury">Projects</a>
            <a onClick={() => { document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); navigate("/contact-us"); }} className="cursor-pointer text-primary hover:text-luxury">Contact</a>
          </div>

          {/* Desktop CTA / User Menu */}
          <div className="hidden lg:flex items-center space-x-4 relative">
            {/* Call Now Icon */}
            <div 
              className="relative cursor-pointer"
              onMouseEnter={() => setShowCallPopup(true)}
              onMouseLeave={() => setShowCallPopup(false)}
            >
              <PhoneCall className="w-6 h-6 text-green-600 hover:text-green-500" />
              {showCallPopup && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-neutral/10 shadow-lg rounded-md p-4 text-sm z-50">
                  <p className="font-semibold text-primary">Call Us Directly</p>
                  <p>CEO: Shiva Chilveri</p>
                  <p className="font-medium text-lg">+91 93737 33473</p>
                  <p className="text-muted-foreground">Available 10 AM - 9 PM</p>
                </div>
              )}
            </div>

            {!user.loggedIn && (
              <Button className="btn-luxury" onClick={() => navigate("/sell-my-property")}>
                Sell my Property
              </Button>
            )}

            {user.loggedIn && (
              <div className="relative">
                <button
                  className="flex items-center gap-2 text-primary hover:text-luxury font-medium"
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                >
                  Hi {user.firstName} {user.lastName} <ChevronDown className="w-4 h-4" />
                </button>

                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral/10 shadow-md rounded-md py-2 z-50">
                     <button
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={() => { navigate("/sell-my-property"); setIsMobileMenuOpen(false); setIsUserDropdownOpen(false);}}
                >
                  Sell My Property
                </button>
                    {/* <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={() => { navigate("/liked-properties"); setIsUserDropdownOpen(false);  setIsUserDropdownOpen(false);}}
                    >
                      Liked Properties
                    </button> */}
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

           {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-primary hover:text-luxury"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-neutral/10 py-4 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-primary font-semibold">
                <Building2 className="w-5 h-5 text-pink-500" />
                <span>Investments</span>
              </div>
              <div className="pl-4 space-y-2">
                {investments.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() =>
                        item.bhk
                          ? handleExplore({ bhk: item.bhk })
                          : handleExplore({ propertyType: item.type })
                      }
                      className="flex items-center gap-2 w-full text-left px-2 py-1 rounded-md hover:bg-gray-50"
                    >
                      <Icon className={`w-5 h-5 ${item.color}`} />
                      <span className="text-sm text-primary flex-1">{item.name}</span>
                      <span className="text-xs text-muted-foreground">{item.price}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t border-neutral/10">
              <a onClick={() => { document.getElementById("about")?.scrollIntoView({ behavior: "smooth" }); setIsMobileMenuOpen(false); }} className="block text-primary hover:text-luxury">About</a>
              <a onClick={() => { document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" }); setIsMobileMenuOpen(false); navigate("/explore-properties"); }} className="block text-primary hover:text-luxury">Projects</a>
              <a onClick={() => { document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); setIsMobileMenuOpen(false); }} className="block text-primary hover:text-luxury">Contact</a>
            </div>

            {!user.loggedIn && (
              <Button className="w-full btn-luxury mt-4" onClick={() => { navigate("/sell-my-property"); setIsMobileMenuOpen(false); }}>
                Sell My Property
              </Button>
            )}

            {user.loggedIn && (
              <div className="border-t border-neutral/10 pt-2">
                <button
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={() => { navigate("/sell-my-property"); setIsMobileMenuOpen(false); }}
                >
                  Sell My Property
                </button>
                {/* <button
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={() => { navigate("/liked-properties"); setIsMobileMenuOpen(false); }}
                >
                  Liked Properties  
                </button> */}
                <button
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
